import { uniqBy } from 'lodash';

import { getFilterableStatus } from '@/utils/getFilterableStatus';

import Constants from '@/constants';

import { ProcessReportsPlanResult } from '@/services/modelHelper';

import { TeamMemberVM } from './types';

import {
  AssignableCourseResult,
  PathwayUser,
  UserChecklistResultDto,
  UserChecklistStatus,
} from '@/_generated/api-clients/xp';

/** @deprecated Uses UserChecklistResultDto (ChecklistDto-shaped); migrate to the Plan-based checklist query. */
export const fromChecklistAndPathwayUsers = (
  checklist: UserChecklistResultDto,
  users: PathwayUser[],
): TeamMemberVM[] => {
  // Create a map of users with formatted names for efficient lookup
  const usersWithNames = users.map(user => ({
    ...user,
    name: user.prefName
      ? `${user.prefName} ${user.lastName}`
      : `${user.firstName} ${user.lastName}`,
    simpleUserName: `${user.prefName ?? user.firstName} ${user.lastName}`,
  }));

  return (
    checklist.status
      ?.map(userChecklist => {
        const user = usersWithNames.find(
          ({ adId }) => userChecklist.userId === adId,
        );
        return {
          ...userChecklist,
          completionDate: userChecklist.completionDate ?? null,
          name: user?.name ?? '',
          simpleUserName: user?.simpleUserName ?? '',
          locations: user?.locationList,
          filterStatus: userChecklist.status
            ? getFilterableStatus(userChecklist.status, false)
            : 'not_completed',
        } satisfies TeamMemberVM;
      })
      .filter(
        teamMember => teamMember.status !== Constants.TRAINING_PLANS.UNASSIGNED,
      ) ?? []
  );
};

/** @deprecated Uses AssignableCourseResult (ChecklistDto-shaped); migrate to the Plan-based checklist query. */
export const fromIdentityUsersAndCourseResults = (
  users: PathwayUser[],
  courses: AssignableCourseResult,
): TeamMemberVM[] =>
  users
    .map(teamMember => {
      const enrollment = courses.enrollments?.[teamMember.adId ?? ''];
      return {
        id: teamMember.adId,
        completedDate: enrollment?.completedDate,
        courseId: enrollment?.courseId ?? '',
        dueDate: enrollment?.dueDate,
        duration: enrollment?.duration,
        enrolledDate: enrollment?.enrolledDate ?? '',
        enrollmentId: enrollment?.enrollmentId ?? '',
        expirationDate: enrollment?.certificate?.expiration ?? '',
        finalScore: enrollment?.finalScore ?? 0,
        hidden: enrollment?.hidden ?? false,
        // Note: locations doesn't exist on ComplianceCourseEnrollmentEntity in generated types
        // Falling back to user's locations as before
        locations: teamMember.locationList,
        mostRecentCompletedDate: enrollment?.mostRecentCompletedDate ?? null,
        name: teamMember.name ?? '',
        simpleUserName: `${teamMember.prefName ?? teamMember.firstName} ${teamMember.lastName}`,
        pathwayCourseId: enrollment?.pathwayCourseId ?? '',
        percentComplete: enrollment?.percentComplete ?? 0,
        startedDate: enrollment?.startedDate ?? '',
        status: enrollment?.status ?? '',
        filterStatus: enrollment
          ? getFilterableStatus(enrollment.status ?? '', !!enrollment.courseId)
          : 'not_completed',
        startDateTime: new Date(teamMember.startDate ?? Infinity).getTime(),
        createdDateTime: new Date(courses.createdDate ?? Infinity).getTime(),
      } satisfies TeamMemberVM;
    })
    .filter(
      (value, index, self) =>
        index === self.findIndex(user => user.id === value.id && !user.hidden),
    );

/** @deprecated Uses UserChecklistStatus (deprecated type); migrate consumers to use UserPlanSummaryReport[]. */
export const fromUserPlanSummaryReport = (
  data: UserChecklistStatus[],
): TeamMemberVM[] =>
  uniqBy(
    data.map(userChecklistStatus => ({
      ...userChecklistStatus.user,
      name: userChecklistStatus.user?.name ?? '',
      simpleUserName: `${userChecklistStatus.user?.prefName ?? userChecklistStatus.user?.firstName} ${userChecklistStatus.user?.lastName}`,
      plansAssigned: userChecklistStatus.plansAssigned,
      plansCompleted: userChecklistStatus.plansCompleted,
      startDateTime: new Date(
        userChecklistStatus.user?.startDate ?? Infinity,
      ).getTime(),
    })),
    'id',
  );

/** @deprecated Uses ProcessReportsPlanResult (ChecklistDto-shaped); will be removed in PATHWAY-3174. */
export const fromReportsPlanResult = (
  data: ProcessReportsPlanResult,
): TeamMemberVM[] => {
  const { status } = data;
  const stepsTotal = data.checklist.stepsTotal ?? 0;
  const vms = status
    .map(
      teamMember =>
        ({
          ...teamMember,
          id: teamMember.id,
          filterStatus: getFilterableStatus(teamMember.status ?? '', false),
          name: teamMember.name,
          percentComplete: stepsTotal
            ? Math.round(((teamMember.stepsComplete ?? 0) / stepsTotal) * 100)
            : 0,
          simpleUserName: `${teamMember.prefName ?? teamMember.firstName} ${teamMember.lastName}`,
          timeTaken: teamMember.timeTakenInSeconds,
        }) satisfies TeamMemberVM,
    )
    .filter(
      teamMember => teamMember.status !== Constants.TRAINING_PLANS.UNASSIGNED,
    );
  return vms;
};
